/* eslint-disable indent */
/* eslint-disable @typescript-eslint/camelcase */
import {
  Grid,
  IconButton,
  makeStyles,
  Tooltip,
  Typography,
  Divider,
} from '@material-ui/core';
import EventBusyIcon from '@material-ui/icons/EventBusy';
import InputIcon from '@material-ui/icons/Input';
import WarningOutlinedIcon from '@material-ui/icons/WarningOutlined';
import clsx from 'clsx';
import Button from 'components/Button';
import ButtonExport from 'components/ButtonExport';
import SearchFilterBar from 'components/SearchFilterBar';
import TableItems, {
  MenuActionItem,
  MenuActions,
  TableCell,
  TableRow,
} from 'components/TableItems';
import Permission from 'components/ValidPermission';
import { requiredPerformancePermissions } from 'containers/Environments/Performance';
import { useTableFilters } from 'hooks/useTableFilters';
import i18n from 'i18n';
import { selectCompany, selectPartner } from 'modules/authenticate/actions';
import {
  CompanyData,
  PartnerData,
  PermissionsData,
} from 'modules/authenticate/interfaces';
import {
  exportEnvironmentSummaryRequest,
  getAllEnvironmentsOperatorRequest,
} from 'modules/environments/actions';
import {
  AllEnvironmentsFields,
  ListAllEnvironmentsDataProps,
} from 'modules/environments/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import ApplicationState from 'store/interfaces';
import { formatDateValue } from 'utils/dateFormat';
import { getCompanyName } from 'utils/tsHelpers';
import EnvironmentsTemplate from '../Templates/Environments';
import { filterOptions } from './filterOptions';
import styles from './styles';
import { getHeaders } from './tableHeaders';
import EnvironmentsDashboardCards from './EnvironmentsDashboardCards';

const useStyles = makeStyles(styles);

export const requiredServerPermissions: Array<keyof PermissionsData> = [
  'list_preparation_servers',
  'list_support_servers',
  'list_models',
  'publish_image',
  'publish_image_v2',
];

interface Props {
  environmentsList: ListAllEnvironmentsDataProps;
  isLoading: boolean;
  partnerSelected: PartnerData | null;
  permissions?: PermissionsData;
  getAllEnvironmentsOperatorRequest: (props) => void;
  selectCompany: (company: CompanyData | null) => void;
  selectPartner: (partner: PartnerData | null) => void;
  exportEnvironmentSummaryRequest: (filtersDashboard) => void;
}

function ClickTypography({
  item,
  value,
  handleClick,
}: {
  item: AllEnvironmentsFields;
  value: string | number;
  handleClick: (item: AllEnvironmentsFields, toUrl: string) => void;
}): JSX.Element {
  const classes = useStyles();

  return (
    <Typography
      className={classes.tableLine}
      onClick={() => {
        handleClick(item, `/environments/${item?.environment_uuid}/instances`);
      }}
    >
      {value}
    </Typography>
  );
}

const AllEnvironmentsList = ({
  environmentsList,
  isLoading,
  permissions,
  getAllEnvironmentsOperatorRequest,
  selectCompany,
  selectPartner,
  exportEnvironmentSummaryRequest,
}: Props) => {
  const classes = useStyles();

  const {
    itemOrder,
    query,
    searchValue,
    setQuery,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    setSearchValue,
  } = useTableFilters();

  const [refresh, setRefresh] = React.useState(false);
  const [showTable, setShowTable] = React.useState(false);

  const history = useHistory();

  const getEnvironments = React.useCallback(
    (refresh = false) => {
      if (!showTable) return;

      getAllEnvironmentsOperatorRequest({
        ...query,
      });

      setRefresh(refresh);
    },
    [getAllEnvironmentsOperatorRequest, query, showTable]
  );

  React.useEffect(() => {
    getEnvironments();
  }, [getEnvironments]);

  const handleClick = (item: AllEnvironmentsFields, toUrl: string) => {
    selectCompany({
      id: item?.company_uuid,
      name: getCompanyName(item),
      shortName: getCompanyName(item),
    });
    selectPartner({
      name: item?.partner_name,
      shortName: item?.partner_name,
      id: item?.partner_uuid,
    });
    history.push(toUrl);
  };

  const renderIconMaintenance = item => {
    if (item?.in_maintenance) {
      return (
        <Tooltip
          placement="bottom"
          arrow
          title={i18n.t('MAINTENANCE_WARNING_KEY')}
        >
          <WarningOutlinedIcon color="error" />
        </Tooltip>
      );
    }
    return <></>;
  };

  const handleToContext = item =>
    handleClick(item, `/environments/${item?.environment_uuid}/instances`);

  const getHeadersPDF = permissions => [
    getHeaders(permissions)
      .filter(header => header.key !== '')
      .map(header => i18n.t(header.i18n)),
  ];

  const getEnvListPDF: Array<[
    string,
    string,
    number,
    number,
    number,
    string
  ]> = environmentsList?.results.map(item => [
    item?.environment_name,
    item?.partner_name,
    item?.num_clients,
    item?.num_users,
    item?.license_limit,
    String(formatDateValue(item?.user_last_access)),
  ]);

  const getCSV = () => {
    exportEnvironmentSummaryRequest({
      query,
    });
  };

  return (
    <EnvironmentsTemplate
      titleContent={i18n.t('ENVIRONMENTS_PAGE.LIST_ENVIRONMENTS_TITLE_KEY')}
      subtitleContent={i18n.t(
        'ENVIRONMENTS_PAGE.LIST_ENVIRONMENTS_SUBTITLE_KEY'
      )}
      actionsContent={
        <Grid className={classes.actionsContainer}>
          <Button
            onClick={() => history.push(`/dashboard/incidents/envStatus`)}
            className={classes.envsToExpire}
            endIcon={<EventBusyIcon />}
          >
            {i18n.t('ENVIRONMENTS_STATUS_TO_EXPIRE_SHORT_KEY')}
          </Button>
          {!!permissions &&
            permissions?.can_export_environment_summary_list && (
              <ButtonExport
                context={i18n.t('ENVIRONMENTS_KEY')}
                getHeadersPDF={getHeadersPDF(permissions)}
                getBodyListPDF={getEnvListPDF}
                csvAction={getCSV}
                filters={query.filters}
                filterOptions={filterOptions}
              />
            )}
        </Grid>
      }
    >
      <EnvironmentsDashboardCards
        filters={query.filters}
        onApplyFilter={() => setShowTable(true)}
        setQuery={setQuery}
      />

      <Divider className={classes.divider} />

      <Grid container className={classes.searchContainer}>
        <SearchFilterBar
          filterOptions={filterOptions}
          isLoading={isLoading}
          searchValue={searchValue}
          query={query}
          onApplyFilters={() => setShowTable(true)}
          setSearchValue={setSearchValue}
          setQuery={setQuery}
          filteredItemsCounterProps={{
            countFilteredItems: environmentsList?.count,
            countTotalItems: environmentsList?.total_count,
            isLoading,
            totalItemsText: i18n.t('MONITOR.ENVIRONMENTS_TOTAL'),
          }}
        />
        {showTable && (
          <TableItems
            header={
              <>
                {getHeaders(permissions).map(item => (
                  <TableCell
                    centerTitle={item?.centerTitle}
                    textAlignCenter={item?.textAlignCenter}
                    style={item?.style}
                    key={item?.key}
                    markOderItem={itemOrder === item?.key}
                    order={item?.order}
                    handleOrder={() =>
                      item?.order ? handleOrder(item?.key) : null
                    }
                  >
                    {i18n.t(item?.i18n)}
                  </TableCell>
                ))}
              </>
            }
            renderItems={(item: AllEnvironmentsFields, index) => (
              <TableRow
                key={item?.environment_uuid}
                className={clsx(classes.summaryRowTable, {
                  [classes.envRow]: !!item?.in_maintenance,
                })}
              >
                <Permission value={'list_environment_instances'}>
                  <TableCell
                    maxLength={10}
                    ellipsis
                    centerTitle
                    className={classes.centerFields}
                    onClick={() => handleToContext(item)}
                  >
                    <IconButton
                      id={`access-environment-${index}`}
                      onClick={() => handleToContext(item)}
                    >
                      <InputIcon className={classes.accessButton} />
                    </IconButton>
                  </TableCell>
                </Permission>
                <TableCell
                  className={classes.longNameField}
                  tooltip={item?.environment_name}
                  onClick={() => handleToContext(item)}
                >
                  <Grid className={classes.textAndIcon}>
                    <ClickTypography
                      item={item}
                      value={item?.environment_name}
                      handleClick={handleClick}
                    />
                    {item?.in_maintenance && renderIconMaintenance(item)}
                  </Grid>
                </TableCell>
                <TableCell
                  className={classes.longNameField}
                  tooltip={item?.partner_name}
                  onClick={() => handleToContext(item)}
                >
                  <ClickTypography
                    item={item}
                    value={item?.partner_name}
                    handleClick={handleClick}
                  />
                </TableCell>

                <TableCell
                  className={classes.numberFields}
                  onClick={() => handleToContext(item)}
                >
                  <ClickTypography
                    item={item}
                    value={item?.num_clients}
                    handleClick={handleClick}
                  />
                </TableCell>
                <TableCell
                  className={classes.numberFields}
                  onClick={() => handleToContext(item)}
                >
                  <ClickTypography
                    item={item}
                    value={item?.num_users}
                    handleClick={handleClick}
                  />
                </TableCell>
                <TableCell
                  className={classes.numberFields}
                  onClick={() => handleToContext(item)}
                >
                  <ClickTypography
                    item={item}
                    value={item?.license_limit}
                    handleClick={handleClick}
                  />
                </TableCell>
                <TableCell
                  className={classes.numberFields}
                  onClick={() => handleToContext(item)}
                >
                  <ClickTypography
                    item={item}
                    value={item?.operational_instances_count}
                    handleClick={handleClick}
                  />
                </TableCell>
                <TableCell
                  className={classes.numberFields}
                  onClick={() => handleToContext(item)}
                >
                  <ClickTypography
                    item={item}
                    value={item?.sessions_count}
                    handleClick={handleClick}
                  />
                </TableCell>
                <TableCell
                  centerTitle
                  textAlignCenter
                  className={classes.dateField}
                  onClick={() => handleToContext(item)}
                >
                  {formatDateValue(item?.user_last_access)}
                </TableCell>
                <TableCell textAlignCenter>
                  <MenuActions id={`btn-options-envs-${index}`}>
                    <Permission value="list_environment_instances">
                      <MenuActionItem
                        id="btn-instances"
                        component={Link}
                        to={`/environments/${item?.environment_uuid}/instances`}
                      >
                        <Typography className={classes.menuOptions}>
                          {i18n.t('INSTANCES_KEY')}
                        </Typography>
                      </MenuActionItem>
                    </Permission>
                    <Permission value={requiredServerPermissions}>
                      <MenuActionItem
                        id="btn-servers"
                        component={Link}
                        to={`/environments/${item?.environment_uuid}/servers`}
                      >
                        <Typography className={classes.menuOptions}>
                          {i18n.t('SERVERS_KEY')}
                        </Typography>
                      </MenuActionItem>
                    </Permission>

                    <Permission value="edit_environment">
                      <MenuActionItem
                        id="btn-configuration"
                        onClick={() => {
                          handleClick(
                            item,
                            `/environments/${item?.environment_uuid}/configuration`
                          );
                        }}
                      >
                        <Typography className={classes.menuOptions}>
                          {i18n.t('SETTINGS_KEY')}
                        </Typography>
                      </MenuActionItem>
                    </Permission>

                    <Permission value="list_environment_instance_sessions">
                      <MenuActionItem
                        id="btn-sessions"
                        onClick={() => {
                          handleClick(
                            item,
                            `/environments/${item?.environment_uuid}/sessions`
                          );
                        }}
                      >
                        <Typography className={classes.menuOptions}>
                          {i18n.t('SESSIONS_KEY')}
                        </Typography>
                      </MenuActionItem>
                    </Permission>

                    <Permission value={requiredPerformancePermissions}>
                      <MenuActionItem
                        id="btn-performance"
                        component={Link}
                        to={`/environments/${item?.environment_uuid}/performance`}
                      >
                        <Typography className={classes.menuOptions}>
                          {i18n.t('PERFORMANCE_PAGE.PERFORMANCE_KEY')}
                        </Typography>
                      </MenuActionItem>
                    </Permission>

                    <MenuActionItem
                      id="btn-logs"
                      component={Link}
                      to={`/environments/${item?.environment_uuid}/history`}
                    >
                      <Typography className={classes.menuOptions}>
                        {i18n.t('HISTORY_SOP_KEY')}
                      </Typography>
                    </MenuActionItem>
                    <MenuActionItem
                      id="btn-maintenance"
                      component={Link}
                      to={`/environments/${item?.environment_uuid}/maintenance`}
                    >
                      <Typography className={classes.menuOptions}>
                        {i18n.t('MAINTENANCE_KEY')}
                      </Typography>
                    </MenuActionItem>
                  </MenuActions>
                </TableCell>
              </TableRow>
            )}
            data={environmentsList}
            isLoading={!refresh && isLoading}
            onChangePage={handlePageChange}
            onChangeLimit={handleLimitChange}
            notFoundText={i18n.t('ENVIRONMENTS_PAGE.NO_ENVIRONMENT_KEY')}
          />
        )}
      </Grid>
    </EnvironmentsTemplate>
  );
};

const mapStateToProps = ({
  authenticate: { data, partnerSelected },
  environments: {
    environmentsList,
    isLoadingDNSReport,
    isLoadingBehaviorReport,
    isLoadingConnectionTypeEnvironmentReport,
    isLoading,
    isLoadingMaintenanceReport,
    isLoadingProductReport,
    isLoadingProductTypeReport,
    isLoadingProviderReport,
    isLoadingRegionReport,
    isLoadingStatusReport,
    isLoadingVirtualizerReport,
    isLoadingZeroTrustReport,
  },
}: ApplicationState) => ({
  isLoading:
    isLoading ||
    isLoadingDNSReport ||
    isLoadingBehaviorReport ||
    isLoadingConnectionTypeEnvironmentReport ||
    isLoadingMaintenanceReport ||
    isLoadingProductReport ||
    isLoadingProductTypeReport ||
    isLoadingProviderReport ||
    isLoadingRegionReport ||
    isLoadingStatusReport ||
    isLoadingVirtualizerReport ||
    isLoadingZeroTrustReport,
  environmentsList,
  partnerSelected,
  permissions: data?.permissions,
});

export default connect(mapStateToProps, {
  selectCompany,
  selectPartner,
  getAllEnvironmentsOperatorRequest,
  exportEnvironmentSummaryRequest,
})(AllEnvironmentsList);
